/**
 * Feed Recommendation Variance Analysis
 * 
 * Calculates recommended daily feed based on SFR (Specific Feeding Rate) 
 * lookup tables and compares with actual feed provided by user.
 * 
 * This module is credit-efficient - it reuses existing data from
 * Spreadsheet Growth Analysis without rebuilding growth curves.
 */

// ============================================================================
// SPECIES-AWARE SFR LOOKUP TABLES
// ============================================================================
// The frontend caches the full SFR table per species. Seabream is bundled for
// zero-latency first render (most common case); Tilapia / Seabass are fetched
// on demand from `GET /api/sfr-table/{species}` to avoid embedding large
// lookup tables in the JS bundle. All SFR values are percentages (e.g., 2.77
// means 2.77% of biomass/day).

const SFR_WEIGHT_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 200, 250, 300, 350, 400, 450, 500];
const SFR_TEMP_VALUES = [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];

// SFR Table: (weight_g, temp_c) => SFR percentage
// Subset of key values for common ranges
const SEABREAM_SFR_TABLE = {
  // Weight 1-10g (fry)
  '1_14': 1.4, '1_16': 1.8, '1_18': 2.3, '1_20': 2.8, '1_22': 3.3, '1_24': 3.7, '1_26': 4.1, '1_28': 4.4, '1_30': 4.5,
  '5_14': 1.2, '5_16': 1.6, '5_18': 2.0, '5_20': 2.5, '5_22': 2.9, '5_24': 3.3, '5_26': 3.7, '5_28': 4.0, '5_30': 4.2,
  '10_14': 1.0, '10_16': 1.4, '10_18': 1.8, '10_20': 2.2, '10_22': 2.6, '10_24': 3.0, '10_26': 3.4, '10_28': 3.7, '10_30': 3.9,
  // Weight 20-50g (juvenile)
  '20_14': 0.9, '20_16': 1.2, '20_18': 1.6, '20_20': 1.9, '20_22': 2.3, '20_24': 2.7, '20_26': 3.0, '20_28': 3.3, '20_30': 3.5,
  '30_14': 0.8, '30_16': 1.1, '30_18': 1.4, '30_20': 1.7, '30_22': 2.1, '30_24': 2.4, '30_26': 2.8, '30_28': 3.0, '30_30': 3.2,
  '40_14': 0.7, '40_16': 1.0, '40_18': 1.3, '40_20': 1.6, '40_22': 1.9, '40_24': 2.2, '40_26': 2.5, '40_28': 2.8, '40_30': 3.0,
  '50_14': 0.7, '50_16': 0.9, '50_18': 1.2, '50_20': 1.5, '50_22': 1.8, '50_24': 2.1, '50_26': 2.4, '50_28': 2.6, '50_30': 2.8,
  // Weight 60-100g (grower)
  '60_14': 0.6, '60_16': 0.9, '60_18': 1.1, '60_20': 1.4, '60_22': 1.7, '60_24': 1.9, '60_26': 2.2, '60_28': 2.4, '60_30': 2.6,
  '70_14': 0.6, '70_16': 0.8, '70_18': 1.1, '70_20': 1.3, '70_22': 1.6, '70_24': 1.8, '70_26': 2.1, '70_28': 2.3, '70_30': 2.5,
  '80_14': 0.6, '80_16': 0.8, '80_18': 1.0, '80_20': 1.2, '80_22': 1.5, '80_24': 1.7, '80_26': 2.0, '80_28': 2.2, '80_30': 2.4,
  '90_14': 0.5, '90_16': 0.7, '90_18': 1.0, '90_20': 1.2, '90_22': 1.4, '90_24': 1.7, '90_26': 1.9, '90_28': 2.1, '90_30': 2.3,
  '100_14': 0.5, '100_16': 0.7, '100_18': 0.9, '100_20': 1.1, '100_22': 1.4, '100_24': 1.6, '100_26': 1.8, '100_28': 2.0, '100_30': 2.2,
  // Weight 110-150g
  '110_14': 0.5, '110_16': 0.7, '110_18': 0.9, '110_20': 1.1, '110_22': 1.3, '110_24': 1.5, '110_26': 1.7, '110_28': 1.9, '110_30': 2.1,
  '120_14': 0.5, '120_16': 0.6, '120_18': 0.8, '120_20': 1.0, '120_22': 1.2, '120_24': 1.5, '120_26': 1.7, '120_28': 1.9, '120_30': 2.0,
  '130_14': 0.4, '130_16': 0.6, '130_18': 0.8, '130_20': 1.0, '130_22': 1.2, '130_24': 1.4, '130_26': 1.6, '130_28': 1.8, '130_30': 2.0,
  '140_14': 0.4, '140_16': 0.6, '140_18': 0.8, '140_20': 0.9, '140_22': 1.1, '140_24': 1.4, '140_26': 1.6, '140_28': 1.7, '140_30': 1.9,
  '150_14': 0.4, '150_16': 0.6, '150_18': 0.7, '150_20': 0.9, '150_22': 1.1, '150_24': 1.3, '150_26': 1.5, '150_28': 1.7, '150_30': 1.8,
  // Weight 200-300g (market size)
  '200_14': 0.4, '200_16': 0.5, '200_18': 0.6, '200_20': 0.8, '200_22': 1.0, '200_24': 1.1, '200_26': 1.3, '200_28': 1.5, '200_30': 1.6,
  '250_14': 0.3, '250_16': 0.4, '250_18': 0.6, '250_20': 0.7, '250_22': 0.9, '250_24': 1.0, '250_26': 1.2, '250_28': 1.3, '250_30': 1.4,
  '300_14': 0.3, '300_16': 0.4, '300_18': 0.5, '300_20': 0.6, '300_22': 0.8, '300_24': 0.9, '300_26': 1.1, '300_28': 1.2, '300_30': 1.3,
  // Weight 350-500g (large)
  '350_14': 0.3, '350_16': 0.4, '350_18': 0.5, '350_20': 0.6, '350_22': 0.7, '350_24': 0.8, '350_26': 1.0, '350_28': 1.1, '350_30': 1.2,
  '400_14': 0.2, '400_16': 0.3, '400_18': 0.4, '400_20': 0.5, '400_22': 0.7, '400_24': 0.8, '400_26': 0.9, '400_28': 1.0, '400_30': 1.1,
  '450_14': 0.2, '450_16': 0.3, '450_18': 0.4, '450_20': 0.5, '450_22': 0.6, '450_24': 0.7, '450_26': 0.8, '450_28': 0.9, '450_30': 1.0,
  '500_14': 0.2, '500_16': 0.3, '500_18': 0.4, '500_20': 0.5, '500_22': 0.6, '500_24': 0.7, '500_26': 0.8, '500_28': 0.9, '500_30': 1.0,
};

/**
 * Find nearest value in an array
 * @param {number} value - Target value
 * @param {number[]} array - Array of values to search
 * @returns {number} Nearest value from array
 */
const findNearest = (value, array) => {
  return array.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
};

// ─────────────────────────────────────────────────────────────
// Per-species SFR table registry. All species — including Seabream —
// are lazy-fetched from the backend so that the authoritative
// `SEABREAM_SFR_TABLE` in /app/backend/sfr_bfcr_tables.py is the
// single source of truth. The bundled `SEABREAM_SFR_TABLE` above is
// retained only as an emergency offline fallback (never used unless
// the fetch fails) — no calculation should silently read from it
// under normal operation.
// ─────────────────────────────────────────────────────────────
const _speciesTables = {};
const _fetchPromises = {};

const _normalizeSpecies = (species) => {
  const s = (species || '').toLowerCase();
  if (s.includes('tilapia') || s.includes('oreochromis')) return 'Tilapia';
  if (s.includes('seabass') || s.includes('dicentrarchus')) return 'Seabass';
  if (s.includes('tripletail') || s.includes('lobotes')) return 'Tripletail';
  return 'Seabream';
};

/**
 * Ensure the SFR table for the active species is loaded. Resolves to the
 * cached table object {weight_values, temp_values, table}. Safe to call
 * repeatedly — memoized per species.
 */
export const ensureSFRTable = async (species) => {
  const key = _normalizeSpecies(species);
  if (_speciesTables[key]) return _speciesTables[key];
  if (_fetchPromises[key]) return _fetchPromises[key];
  const API = process.env.REACT_APP_BACKEND_URL || '';
  _fetchPromises[key] = fetch(`${API}/api/sfr-table/${encodeURIComponent(key)}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      if (!j || !j.table) return null;
      _speciesTables[key] = {
        weight_values: j.weight_values,
        temp_values: j.temp_values,
        table: j.table,
      };
      return _speciesTables[key];
    })
    .catch(() => null);
  return _fetchPromises[key];
};

/**
 * Lookup SFR (Specific Feeding Rate) percentage for a species.
 * Uses nearest match for weight and temperature — no interpolation.
 *
 * @param {number} weightG - Fish weight in grams
 * @param {number} temperatureC - Water temperature in Celsius
 * @param {string} species - Species name (defaults to Seabream for
 *   backwards compatibility). If the table for the requested species
 *   hasn't been loaded via `ensureSFRTable(species)` yet, this falls
 *   through to the Seabream table.
 * @returns {number|null} SFR percentage (e.g., 1.5 = 1.5% of body
 *   weight per day) or null when inputs are missing.
 */
export const lookupSFR = (weightG, temperatureC, species = 'Seabream') => {
  if (weightG === null || weightG === undefined || temperatureC === null || temperatureC === undefined) {
    return null;
  }

  const key = _normalizeSpecies(species);
  const ref = _speciesTables[key];
  // If the authoritative table hasn't loaded yet, return null; the caller's
  // useMemo re-runs once `ensureSFRTable(species)` resolves and bumps
  // `sfrTableVersion`. This avoids silently serving stale/bundled values.
  if (!ref) return null;
  const { weight_values: weights, temp_values: temps, table } = ref;

  const minT = temps[0];
  const maxT = temps[temps.length - 1];
  // Do NOT Math.round the temperature before snapping — the backend table
  // stores 0.5°C-granular Seabream entries, and rounding to int would
  // silently collapse half-steps (e.g. 18.5 → 19) and miss the exact cell
  // that the authoritative `lookup_sfr` picks. `findNearest` on the actual
  // temp_values array does the correct snap regardless of granularity.
  const clampedTemp = Math.max(minT, Math.min(maxT, temperatureC));
  const nearestWeight = findNearest(weightG, weights);
  const nearestTemp = findNearest(clampedTemp, temps);

  const lookupKey = `${nearestWeight}_${nearestTemp}`;
  const sfr = table[lookupKey];
  if (sfr !== undefined) return sfr;

  // Fallback 1 — adjacent temperatures at the same weight (covers half-step
  // temperatures that collapse to the same int on the backend).
  for (let offset = 1; offset <= 3; offset++) {
    const keyBelow = `${nearestWeight}_${nearestTemp - offset}`;
    const keyAbove = `${nearestWeight}_${nearestTemp + offset}`;
    if (table[keyBelow] !== undefined) return table[keyBelow];
    if (table[keyAbove] !== undefined) return table[keyAbove];
  }

  // Fallback 2 — adjacent weights at the same temperature.
  const wIdx = weights.indexOf(nearestWeight);
  if (wIdx >= 0) {
    const maxOffset = Math.max(wIdx, weights.length - 1 - wIdx);
    for (let offset = 1; offset <= maxOffset; offset++) {
      const wBelow = weights[wIdx - offset];
      const wAbove = weights[wIdx + offset];
      if (wBelow !== undefined && table[`${wBelow}_${nearestTemp}`] !== undefined) {
        return table[`${wBelow}_${nearestTemp}`];
      }
      if (wAbove !== undefined && table[`${wAbove}_${nearestTemp}`] !== undefined) {
        return table[`${wAbove}_${nearestTemp}`];
      }
    }
  }

  // Fallback 3 — nearest existing entry anywhere in the table by Manhattan
  // distance (weight index + |Δtemp|). The bundled Seabream table only has
  // entries at even temperatures (14,16,18…30) and at a subset of weights
  // (1,5,10,20…500) — so a fry at 4g / 17°C falls through fallbacks 1 & 2
  // (no entry at weight 4 at any temp, no entry at temp 17 at any weight).
  // Without this final sweep, Rec. Feed would stay "—" on that cohort's
  // entire early-stage life. We prefer the closest weight band at the
  // nearest available temperature.
  let bestVal = null;
  let bestDist = Infinity;
  const baseWIdx = wIdx >= 0 ? wIdx : 0;
  for (let i = 0; i < weights.length; i++) {
    for (let j = 0; j < temps.length; j++) {
      const cand = table[`${weights[i]}_${temps[j]}`];
      if (cand === undefined) continue;
      const dist = Math.abs(i - baseWIdx) + Math.abs(temps[j] - nearestTemp);
      if (dist < bestDist) {
        bestDist = dist;
        bestVal = cand;
      }
    }
  }
  return bestVal;
};

// ============================================================================
// FEED VARIANCE CALCULATIONS
// ============================================================================

/**
 * Classification thresholds
 */
export const FEED_VARIANCE_THRESHOLDS = {
  OVERFEED_THRESHOLD: 5,    // > +5% = Overfeeding
  UNDERFEED_THRESHOLD: -5,  // < -5% = Underfeeding
};

/**
 * Classify feeding status based on variance percentage
 * @param {number} variancePercent - Total feed variance as percentage
 * @returns {Object} Classification with status and color
 */
export const classifyFeedingStatus = (variancePercent) => {
  if (variancePercent === null || variancePercent === undefined || isNaN(variancePercent)) {
    return { status: 'Unknown', color: 'gray', description: 'Unable to calculate feeding status' };
  }
  
  if (variancePercent > FEED_VARIANCE_THRESHOLDS.OVERFEED_THRESHOLD) {
    return { 
      status: 'Overfeeding', 
      color: 'red',
      description: `Actual feed exceeds recommendation by ${Math.abs(variancePercent).toFixed(1)}%`
    };
  } else if (variancePercent < FEED_VARIANCE_THRESHOLDS.UNDERFEED_THRESHOLD) {
    return { 
      status: 'Underfeeding', 
      color: 'amber',
      description: `Actual feed is ${Math.abs(variancePercent).toFixed(1)}% below recommendation`
    };
  } else {
    return { 
      status: 'In Line', 
      color: 'green',
      description: 'Feeding is within acceptable range (±5% of recommendation)'
    };
  }
};

/**
 * Calculate feed recommendation analysis for spreadsheet data
 * 
 * @param {Array} dailyRows - Array of daily data rows from spreadsheet analysis
 *   Each row must have: date, actual_weight (g), temperature, feed (kg)
 * @param {number} fishCount - Current fish count (after mortality adjustments)
 * @param {number} initialFishCount - Starting fish count
 * @returns {Object} Feed analysis results
 */
export const calculateFeedRecommendationAnalysis = (dailyRows, fishCount, initialFishCount) => {
  if (!dailyRows || dailyRows.length === 0) {
    return {
      available: false,
      message: 'No daily data available for feed analysis',
      dailyAnalysis: [],
      totals: null,
      classification: null,
    };
  }
  
  // Track running fish count (decrease with mortality)
  let currentFishCount = initialFishCount || fishCount;
  
  const dailyAnalysis = [];
  let totalActualFeed = 0;
  let totalRecommendedFeed = 0;
  let daysWithFeedData = 0;
  let daysWithRecommendation = 0;
  
  for (const row of dailyRows) {
    const { date, actual_weight, temperature, feed, mortality } = row;
    
    // Skip rows without weight or temperature (can't calculate recommendation)
    if (actual_weight === null || actual_weight === undefined || 
        temperature === null || temperature === undefined) {
      dailyAnalysis.push({
        date,
        actual_feed_kg: feed || null,
        recommended_feed_kg: null,
        variance_kg: null,
        variance_percent: null,
        sfr_percent: null,
        biomass_kg: null,
        reason: 'Missing weight or temperature data',
      });
      
      // Still count actual feed towards totals
      if (feed !== null && feed !== undefined && feed > 0) {
        totalActualFeed += feed;
        daysWithFeedData++;
      }
      continue;
    }
    
    // Adjust fish count for mortality
    if (mortality && mortality > 0) {
      currentFishCount = Math.max(0, currentFishCount - mortality);
    }
    
    // Calculate biomass (kg)
    const biomassKg = (currentFishCount * actual_weight) / 1000;
    
    // Lookup SFR percentage
    const sfrPercent = lookupSFR(actual_weight, temperature);
    
    // Calculate recommended feed (kg)
    // recommended_feed_kg = biomass_kg × (SFR_percent / 100)
    const recommendedFeedKg = sfrPercent !== null 
      ? biomassKg * (sfrPercent / 100) 
      : null;
    
    // Calculate variance
    let varianceKg = null;
    let variancePercent = null;
    
    const actualFeedKg = feed !== null && feed !== undefined ? feed : null;
    
    if (actualFeedKg !== null && recommendedFeedKg !== null && recommendedFeedKg > 0) {
      // feed_variance_kg = actual_feed_kg − recommended_feed_kg
      varianceKg = actualFeedKg - recommendedFeedKg;
      
      // feed_variance_percent = (variance_kg / recommended_feed_kg) × 100
      variancePercent = (varianceKg / recommendedFeedKg) * 100;
      
      daysWithFeedData++;
    }
    
    if (recommendedFeedKg !== null) {
      totalRecommendedFeed += recommendedFeedKg;
      daysWithRecommendation++;
    }
    
    if (actualFeedKg !== null) {
      totalActualFeed += actualFeedKg;
    }
    
    dailyAnalysis.push({
      date,
      actual_feed_kg: actualFeedKg,
      recommended_feed_kg: recommendedFeedKg !== null ? Math.round(recommendedFeedKg * 100) / 100 : null,
      variance_kg: varianceKg !== null ? Math.round(varianceKg * 100) / 100 : null,
      variance_percent: variancePercent !== null ? Math.round(variancePercent * 10) / 10 : null,
      sfr_percent: sfrPercent,
      biomass_kg: Math.round(biomassKg * 100) / 100,
      fish_count: currentFishCount,
    });
  }
  
  // Calculate totals
  const totalVarianceKg = totalActualFeed - totalRecommendedFeed;
  const totalVariancePercent = totalRecommendedFeed > 0 
    ? (totalVarianceKg / totalRecommendedFeed) * 100 
    : null;
  
  // Get classification
  const classification = classifyFeedingStatus(totalVariancePercent);
  
  return {
    available: daysWithFeedData > 0 && daysWithRecommendation > 0,
    message: daysWithFeedData === 0 
      ? 'No feed data provided in spreadsheet' 
      : daysWithRecommendation === 0 
        ? 'Unable to calculate feed recommendations (missing weight/temperature data)'
        : null,
    dailyAnalysis,
    totals: {
      total_actual_feed_kg: Math.round(totalActualFeed * 100) / 100,
      total_recommended_feed_kg: Math.round(totalRecommendedFeed * 100) / 100,
      total_variance_kg: Math.round(totalVarianceKg * 100) / 100,
      total_variance_percent: totalVariancePercent !== null 
        ? Math.round(totalVariancePercent * 10) / 10 
        : null,
      days_with_feed_data: daysWithFeedData,
      days_with_recommendation: daysWithRecommendation,
    },
    classification,
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  lookupSFR,
  calculateFeedRecommendationAnalysis,
  classifyFeedingStatus,
  FEED_VARIANCE_THRESHOLDS,
};
